import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, X, Eye, EyeOff } from "lucide-react";
import { authApi } from "@/lib/api";
import { getApiError } from "@/lib/utils";

/** Modal for the current user to change their own password.
 *  When `forced=true`, cannot be dismissed and shows a warning banner. */
export default function ChangePasswordModal({ open, forced = false, onClose, onSuccess }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await authApi.changePassword(current, next);
      toast.success("Password updated");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(getApiError(err, "Password change failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={forced ? undefined : onClose}
      data-testid="change-password-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md rounded-md border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-nmi-700">Security</div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Change password</h3>
            {forced ? (
              <p className="mt-1 text-[11px] font-semibold text-amber-800">
                Your admin issued a temporary password. Choose a new one to continue.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-slate-500">Choose a strong password — 8+ characters.</p>
            )}
          </div>
          {!forced && (
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Close">
              <X size={18} />
            </button>
          )}
        </div>
        <div className="px-6 py-5 space-y-3">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              Current password
            </label>
            <input
              type={showPw ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="cpm-current"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-nmi-600"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              New password
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="cpm-new"
                className="w-full rounded-md border border-slate-200 px-3 py-2 pr-10 text-sm outline-none focus:border-nmi-600"
              />
              <button
                type="button"
                aria-label={showPw ? "Hide password" : "Show password"}
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:text-slate-700"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              Confirm new password
            </label>
            <input
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              data-testid="cpm-confirm"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-nmi-600"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-3">
          {!forced && (
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            data-testid="cpm-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
            Update password
          </button>
        </div>
      </form>
    </div>
  );
}
